<template>
  <Container class="tw-mt-8">
    <div class="tw-mx-auto tw-max-w-5xl">
      <BaseButton
        class="tw-mb-6"
        color="outline-gray"
        href="/collections"
        is-inertia-link
      >
        <ArrowLeftIcon class="tw-mr-2 tw-h-4 tw-w-4" />
        Back to Collections
      </BaseButton>
      <Card>
        <div
          class="tw-flex tw-flex-col tw-space-y-4 md:tw-flex-row md:tw-space-x-8 md:tw-space-y-0"
        >
          <div>
            <h1 class="tw-heading-1">{{ collection.name }}</h1>
            <div class="tw--ml-0.5 tw-mt-1.5">
              <Tag
                v-for="(tag, index) in collection.collectionTagList"
                :key="index"
                size="small"
                class="tw-m-0.5"
              >
                {{ tag }}
              </Tag>
            </div>
            <p class="tw-mt-4 tw-text-sm tw-text-neutral-500">
              {{ collection.description }}
            </p>
          </div>
        </div>

        <div
          v-if="isUserLoggedIn"
          class="tw-mt-4 tw-flex tw-flex-col tw-items-center tw-space-y-2 xs:tw-flex-row xs:tw-space-x-2 xs:tw-space-y-0"
        >
          <CollectionFollowButton :collection="collection" />
          <div v-if="hasRole(ROLE.MENTOR)">
            <BaseButton
              v-if="canJoinToCollection"
              color="primary"
              @click="openModal(requestInviteModalName)"
              test_id="request-invite-button"
            >
              Join this collection
            </BaseButton>
            <BaseButton
              v-else-if="isInviteRequestSent"
              color="primary"
              is-disabled
            >
              Invite request sent...
            </BaseButton>
            <BaseButton
              v-else
              color="red"
              @click="openModal(leaveConfirmationModalName)"
              test_id="leave-collection-button"
            >
              Leave this collection
            </BaseButton>

            <CollectionRequestInviteModal
              :name="requestInviteModalName"
              :collection-id="collection.id"
            />
            <ConfirmationModal
              :name="leaveConfirmationModalName"
              header="Are you sure you want to leave this collection?"
              confirmation-button-text="Yes, leave"
              @confirmation="leaveCollection"
            >
              <template #icon>
                <LeaveIcon />
              </template>
            </ConfirmationModal>
          </div>
        </div>

        <div v-if="members.length" class="tw-mt-4">
          <div
            class="tw-hidden tw-w-full tw-items-center tw-border-b tw-border-t tw-border-neutral-200 tw-py-2 tw-text-xs tw-font-medium tw-uppercase tw-text-neutral-500 md:tw-inline-flex"
          >
            <div class="tw-w-full tw-grow">Mentors ({{ members.length }})</div>
            <div class="tw-w-20 tw-shrink-0 tw-text-center">Rate</div>
            <div class="tw-w-28 tw-shrink-0 tw-text-center">Sessions</div>
            <div class="tw-w-20 tw-shrink-0 tw-text-center">Rating</div>
            <div class="tw-w-40 tw-shrink-0" />
          </div>
          <div
            v-for="member in members"
            :key="member.id"
            class="tw-flex tw-w-full tw-flex-col tw-items-center tw-space-y-3 tw-border-b tw-border-neutral-200 tw-py-3 tw-text-sm md:tw-flex-row md:tw-space-y-0"
          >
            <div
              class="tw-flex tw-grow tw-items-center tw-space-x-3 md:tw-w-full"
            >
              <InertiaLink :href="member.profileUrl">
                <Avatar
                  :src="member.avatarUrl"
                  :alt="`${member.name}'s avatar`"
                  size="lg"
                />
              </InertiaLink>
              <div>
                <InertiaLink :href="member.profileUrl" class="tw-font-semibold">
                  {{ member.name }}
                </InertiaLink>
                <div class="tw-text-xs tw-text-neutral-400">
                  {{ member.shortLivingInWithTimezone }}
                </div>
              </div>
            </div>
            <div class="md:tw-w-20 md:tw-shrink-0 md:tw-text-center">
              <span class="md:tw-hidden">Rate:</span>
              {{ member.hourRateInDollars }}/h
            </div>
            <div class="md:tw-w-28 md:tw-shrink-0 md:tw-text-center">
              <span class="md:tw-hidden">Sessions:</span>
              {{ member.coursesCount }}
            </div>
            <div class="md:tw-w-20 md:tw-shrink-0 md:tw-text-center">
              <span class="md:tw-hidden">Rating:</span>
              {{ member.overallRating }}
            </div>
            <div class="md:tw-w-40 md:tw-shrink-0 md:tw-text-right">
              <BaseButton
                :href="member.profileUrl"
                target="_blank"
                color="outline-secondary"
              >
                View profile
              </BaseButton>
            </div>
          </div>
        </div>
      </Card>
    </div>
  </Container>
</template>

<script setup>
import BaseButton from '@/shared/components/BaseButton.vue'
import Container from '@/shared/components/Container.vue'
import ArrowLeftIcon from '@/assets/images/icons/arrow-left.svg'
import Card from '@/shared/components/Card.vue'
import Tag from '@/shared/components/Tag.vue'
import CollectionRequestInviteModal from '@/modules/growthmentor/collections/components/CollectionRequestInviteModal.vue'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal.vue'
import LeaveIcon from '@/assets/images/icons/leave.svg'
import CollectionFollowButton from '@/modules/growthmentor/collections/components/CollectionFollowButton.vue'
import { OBJECT_PERMISSIONS, ROLE } from '@/shared/data/permissionSystem.js'
import { computed } from 'vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { router } from '@inertiajs/vue3'
import { useModals } from '@/shared/composables/useModals.js'

const props = defineProps({
  collection: {
    type: Object,
    required: true,
  },
  members: {
    type: Array,
    required: true,
  },
})

const { openModal } = useModals()
const { hasObjectPermissions, isUserLoggedIn, hasRole } = usePermissionSystem()

const requestInviteModalName = computed(() => {
  return `collection-request-invite-modal-${props.collection.id}`
})
const leaveConfirmationModalName = computed(() => {
  return `collection-leave-confirmation-modal-${props.collection.id}`
})
const isInviteRequestSent = computed(() => {
  return hasObjectPermissions(
    props.collection,
    OBJECT_PERMISSIONS.COLLECTIONS.REQUESTED,
  )
})
const canJoinToCollection = computed(() => {
  return (
    hasObjectPermissions(
      props.collection,
      OBJECT_PERMISSIONS.COLLECTIONS.NOT_APPLIED,
    ) ||
    hasObjectPermissions(
      props.collection,
      OBJECT_PERMISSIONS.COLLECTIONS.DECLINED,
    )
  )
})

const leaveCollection = () => {
  router.delete(`/collections/${props.collection.id}/members`, {
    preserveScroll: true,
  })
}
</script>
